import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Modal, Spinner, Row, Col, Input,
  Nav, NavItem, NavLink, TabContent, TabPane, Card, CardBody, Badge,
} from 'reactstrap';
import { motion } from 'framer-motion';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import {
  fetchAsc,
  fetchMaterioActivity,
  createAsc,
  updateAsc,
  changeStatusAsc,
  clearMaterioMessages,
} from '../../store/materiovigilance/actions';
import { formatCreatedDate } from '../../constants/utils';
import usePermission from '../../Hooks/usePermission';
import {
  MATERIOVIGILANCE_UPDATE,
  MATERIOVIGILANCE_CLOSE,
  MATERIOVIGILANCE_REOPEN,
} from '../../constants/permissions';
import AssetMultiPicker from './AssetMultiPicker';
import RecipientUserMultiPicker from './RecipientUserMultiPicker';
import MaterioStatusBadge from './MaterioStatusBadge';
import materioMultiSelectStyles from './materioSelectStyles';
import AssetListTable from '../../components/Common/Table/AssetListTable';
import AssetModal from '../../components/AssetModal';
import api from '../../helpers/api';

const STATUSES = ['DRAFT', 'DECLARED', 'UNDER_INVESTIGATION', 'CLOSED'];

const STATUS_OPTIONS = (t) => [
  {
    value: 'DRAFT',
    icon: 'edit-3',
    label: t('compliance.materiovigilance.status.draft', 'Draft'),
    description: t('compliance.materiovigilance.status_descriptions.draft', 'Initial draft, not yet declared'),
  },
  {
    value: 'DECLARED',
    icon: 'send',
    label: t('compliance.materiovigilance.status.declared', 'Declared'),
    description: t('compliance.materiovigilance.status_descriptions.declared', 'Officially declared'),
  },
  {
    value: 'UNDER_INVESTIGATION',
    icon: 'search',
    label: t('compliance.materiovigilance.status.under_investigation', 'Under investigation'),
    description: t('compliance.materiovigilance.status_descriptions.under_investigation', 'Investigation in progress'),
  },
  {
    value: 'CLOSED',
    icon: 'check-circle',
    label: t('compliance.materiovigilance.status.closed', 'Closed'),
    description: t('compliance.materiovigilance.status_descriptions.closed', 'Investigation closed'),
  },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const emptyForm = () => ({
  id: null,
  incidentDate: todayIso(),
  description: '',
  isRecurring: null,
  patientCount: 0,
  hasClinicalImplications: null,
  isBiologicallyContaminated: null,
  stillInUse: null,
  status: 'DRAFT',
  linkedAssetIds: [],
  recipientUserIds: [],
});

const MaterioAscendingModal = ({ isOpen, toggle, mode: initialMode, recordId = null, onChanged }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();
  const { currentAsc, currentAscLoading, saving, saveError, activity } = useSelector((s) => s.Materiovigilance);
  const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
  const orgId = authUser?.activeOrganizationId ?? null;

  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState(emptyForm());
  const [validationError, setValidationError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [assetModal, setAssetModal] = useState({ isOpen: false, userData: null, loading: false });

  const handleAssetClick = async (asset) => {
    if (!asset?.id) return;
    setAssetModal({ isOpen: true, userData: asset, loading: true });
    try {
      const { data } = await api.get(`asset/get-by-id?id=${asset.id}`);
      const full = data?.response || asset;
      setAssetModal({ isOpen: true, userData: full, loading: false });
    } catch (e) {
      setAssetModal((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setMode(initialMode);
    setValidationError(null);
    setActiveTab('details');
    if (initialMode === 'create') {
      setForm(emptyForm());
    } else if (recordId) {
      dispatch(fetchAsc(recordId));
      dispatch(fetchMaterioActivity('ASCENDING', recordId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialMode, recordId]);

  useEffect(() => {
    if (!isOpen || initialMode === 'create' || !currentAsc) return;
    setForm({
      id: currentAsc.id,
      incidentDate: currentAsc.incidentDate || todayIso(),
      description: currentAsc.description || '',
      isRecurring: currentAsc.isRecurring ?? null,
      patientCount: currentAsc.patientCount ?? 0,
      hasClinicalImplications: currentAsc.hasClinicalImplications ?? null,
      isBiologicallyContaminated: currentAsc.isBiologicallyContaminated ?? null,
      stillInUse: currentAsc.stillInUse ?? null,
      status: currentAsc.status || 'DRAFT',
      linkedAssetIds: currentAsc.linkedAssetIds ? Array.from(currentAsc.linkedAssetIds) : [],
      recipientUserIds: currentAsc.recipientUserIds ? Array.from(currentAsc.recipientUserIds) : [],
    });
  }, [currentAsc, isOpen, initialMode]);

  useEffect(() => () => dispatch(clearMaterioMessages()), [dispatch]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const ynOptions = useMemo(() => [
    { value: true, label: t('shared.yes', 'Yes') },
    { value: false, label: t('shared.no', 'No') },
  ], [t]);
  const statusOptions = useMemo(() => STATUSES.map((s) => ({
    value: s,
    label: t(`compliance.materiovigilance.status.${s.toLowerCase()}`, s),
  })), [t]);

  const validate = () => {
    if (!form.incidentDate) return t('validation.materiovigilance.incident_date.required', 'Incident date is required');
    if (!form.description.trim()) return t('validation.materiovigilance.description.required', 'Description is required');
    if (form.isRecurring === null) return t('validation.materiovigilance.is_recurring.required', 'Answer the recurring question');
    if (form.patientCount === null || form.patientCount < 0) return t('validation.materiovigilance.patient_count.required', 'Patient count is required');
    if (form.hasClinicalImplications === null) return t('validation.materiovigilance.has_clinical_implications.required', 'Answer the clinical implications question');
    if (form.stillInUse === null) return t('validation.materiovigilance.still_in_use.required', 'Answer the still-in-use question');
    if (!form.linkedAssetIds.length) return t('validation.materiovigilance.linked_assets.required', 'Link at least one asset');
    return null;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const err = validate();
    if (err) { setValidationError(err); return; }
    setValidationError(null);
    const payload = { ...form };
    if (mode === 'edit' && form.id) {
      dispatch(updateAsc(payload, () => { if (onChanged) onChanged(); toggle(); }));
    } else {
      dispatch(createAsc(payload, () => { if (onChanged) onChanged(); toggle(); }));
    }
  };

  const handleStatusSelect = (toStatus) => {
    if (!toStatus || !form.id || toStatus === currentAsc?.status) return;
    dispatch(changeStatusAsc({ id: form.id, toStatus }, () => {
      dispatch(fetchAsc(form.id));
      dispatch(fetchMaterioActivity('ASCENDING', form.id));
      if (onChanged) onChanged();
    }));
  };

  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isCreate = mode === 'create';

  const canSelectStatus = (target) => {
    if (target === currentAsc?.status) return true;
    if (target === 'CLOSED') return hasPermission(MATERIOVIGILANCE_CLOSE);
    if (currentAsc?.status === 'CLOSED') return hasPermission(MATERIOVIGILANCE_REOPEN);
    return hasPermission(MATERIOVIGILANCE_UPDATE);
  };

  const titleText = isCreate
    ? t('compliance.materiovigilance.ascending.new', 'New ascending materiovigilance')
    : isEdit
      ? t('compliance.materiovigilance.ascending.edit', 'Edit ascending materiovigilance')
      : (currentAsc?.alertNumber || t('compliance.materiovigilance.ascending.view', 'Materiovigilance — ascending'));

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size={isView ? 'xl' : 'lg'}
      centered
      className="modern-modal"
      backdrop="static"
      style={{ maxHeight: '90vh' }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="modal-content border-0 shadow-lg"
        style={{ borderRadius: '12px', overflow: 'hidden' }}
      >
        {isView ? (
          <div
            className="modal-header border-0 text-white position-sticky d-flex justify-content-between align-items-center"
            style={{
              top: 0,
              zIndex: 10,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              padding: '1.5rem',
            }}
          >
            <div className="d-flex align-items-center">
              <div className="me-3">
                <div
                  className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: 48, height: 48 }}
                >
                  <FeatherIcon icon="alert-triangle" size={24} className="text-white" />
                </div>
              </div>
              <div className="flex-grow-1">
                <h4 className="modal-title mb-1 text-white d-flex align-items-center">
                  <span className="me-2">{currentAsc?.alertNumber || titleText}</span>
                  {currentAsc?.status && <MaterioStatusBadge status={currentAsc.status} />}
                </h4>
                <div className="d-flex align-items-center gap-3">
                  {currentAsc?.incidentDate && (
                    <span className="text-white small">
                      <FeatherIcon icon="calendar" size={14} className="me-1" />
                      {formatCreatedDate(currentAsc.incidentDate)}
                    </span>
                  )}
                  {currentAsc?.issuerUserName && (
                    <span className="text-white small">
                      <FeatherIcon icon="user" size={14} className="me-1" />
                      {currentAsc.issuerUserName}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3 ms-auto">
              {hasPermission(MATERIOVIGILANCE_UPDATE) && (
                <button
                  type="button"
                  className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                  onClick={() => setMode('edit')}
                  title={t('shared.edit', 'Edit')}
                  style={{
                    background: 'rgba(255,255,255,0.95)',
                    color: '#495057',
                    fontWeight: 500,
                    borderRadius: 8,
                  }}
                >
                  <FeatherIcon icon="edit-3" size={16} />
                  <span>{t('shared.edit', 'Edit')}</span>
                </button>
              )}
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={toggle}
                aria-label={t('shared.close', 'Close')}
              />
            </div>
          </div>
        ) : (
          <div className="modal-header">
            <div className="d-flex align-items-center gap-2">
              <h5 className="modal-title fs-4 mb-0">{titleText}</h5>
            </div>
            <button type="button" className="btn-close" onClick={toggle} aria-label={t('shared.close', 'Close')} />
          </div>
        )}

        <div className={isView ? 'modal-body p-0' : 'modal-body p-4'}
             style={{ maxHeight: isView ? 'calc(90vh - 220px)' : 'calc(90vh - 140px)', overflow: 'auto' }}>
          {validationError && <div className="alert alert-warning m-3 mb-0">{validationError}</div>}
          {saveError && <div className="alert alert-danger m-3 mb-0">{String(saveError)}</div>}

          {currentAscLoading && !isCreate ? (
            <div className="text-center py-5"><Spinner /></div>
          ) : isView ? (
            <AscendingViewTabs
              record={currentAsc}
              activity={activity}
              t={t}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              canSelectStatus={canSelectStatus}
              onStatusSelect={handleStatusSelect}
              onAssetClick={handleAssetClick}
            />
          ) : (
            <AscendingFormBody
              form={form}
              set={set}
              orgId={orgId}
              t={t}
              mode={mode}
              ynOptions={ynOptions}
              statusOptions={statusOptions}
            />
          )}
        </div>

        {!isView && (
          <div className="modal-footer bg-light border-top-0">
            <button type="button" className="btn btn-outline-secondary" onClick={toggle}>{t('shared.cancel', 'Cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? <><Spinner size="sm" className="me-2" />{t('shared.saving', 'Saving…')}</>
                : isEdit ? t('shared.update', 'Update') : t('shared.create', 'Create')}
            </button>
          </div>
        )}
      </motion.div>
      <AssetModal
        isOpen={assetModal.isOpen}
        toggle={() => setAssetModal((prev) => ({ ...prev, isOpen: false }))}
        userData={assetModal.userData}
        loading={assetModal.loading}
        formatCreatedDate={formatCreatedDate}
      />
    </Modal>
  );
};

// ----- form body ------------------------------------------------------------

const FieldRow = ({ label, required, children }) => (
  <Row className="mb-3">
    <Col md={12}>
      <label className="form-label fw-medium">
        {label}
        {required && <span className="text-danger"> *</span>}
      </label>
      {children}
    </Col>
  </Row>
);

const YnSelect = ({ value, onChange, options, t }) => (
  <Select
    options={options}
    value={value === null || value === undefined ? null : options.find((o) => o.value === value) || null}
    onChange={(opt) => onChange(opt ? opt.value : null)}
    classNamePrefix="react-select"
    styles={materioMultiSelectStyles}
    isClearable
    placeholder={t('compliance.materiovigilance.placeholder.select', 'Select…')}
  />
);

const AscendingFormBody = ({ form, set, orgId, t, mode, ynOptions, statusOptions }) => (
  <>
    <FieldRow label={t('compliance.materiovigilance.ascending.field.incident_date', 'Date of incident')} required>
      <Input type="date" value={form.incidentDate} onChange={(e) => set('incidentDate', e.target.value)} />
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.ascending.field.description', 'Description')} required>
      <Input type="textarea" rows={5} value={form.description} onChange={(e) => set('description', e.target.value)} />
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.ascending.field.is_recurring', 'Is the incident recurring?')} required>
      <YnSelect value={form.isRecurring} onChange={(v) => set('isRecurring', v)} options={ynOptions} t={t} />
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.ascending.field.patient_count', 'Number of patients affected')} required>
      <Input type="number" min="0" value={form.patientCount} onChange={(e) => set('patientCount', e.target.value === '' ? null : Number(e.target.value))} />
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.ascending.field.has_clinical_implications', 'Were there clinical implications on patient(s)?')} required>
      <YnSelect value={form.hasClinicalImplications} onChange={(v) => set('hasClinicalImplications', v)} options={ynOptions} t={t} />
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.ascending.field.is_biologically_contaminated', 'Is the device contaminated with a biological fluid?')}>
      <YnSelect value={form.isBiologicallyContaminated} onChange={(v) => set('isBiologicallyContaminated', v)} options={ynOptions} t={t} />
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.ascending.field.still_in_use', 'Is the device still in use?')} required>
      <YnSelect value={form.stillInUse} onChange={(v) => set('stillInUse', v)} options={ynOptions} t={t} />
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.ascending.field.linked_assets', 'Assets')} required>
      <AssetMultiPicker organizationId={orgId} value={form.linkedAssetIds} onChange={(v) => set('linkedAssetIds', v)} />
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.ascending.field.recipients', 'Recipients')}>
      <RecipientUserMultiPicker
        organizationId={orgId}
        value={form.recipientUserIds}
        onChange={(v) => set('recipientUserIds', v)}
      />
    </FieldRow>
    {mode === 'create' && (
      <FieldRow label={t('compliance.materiovigilance.ascending.field.initial_status', 'Initial status')} required>
        <Select
          options={statusOptions}
          value={statusOptions.find((o) => o.value === form.status) || null}
          onChange={(opt) => set('status', opt ? opt.value : 'DRAFT')}
          classNamePrefix="react-select"
          styles={materioMultiSelectStyles}
          placeholder={t('compliance.materiovigilance.placeholder.select', 'Select…')}
        />
      </FieldRow>
    )}
  </>
);

// ----- view tabs ------------------------------------------------------------

const TAB_DEFS = (t) => [
  { key: 'details',    label: t('shared.details', 'Details'),                                 icon: 'file-text' },
  { key: 'management', label: t('compliance.materiovigilance.tab.management', 'Management'), icon: 'sliders' },
  { key: 'assets',     label: t('compliance.materiovigilance.tab.assets', 'Assets'),         icon: 'package' },
  { key: 'activity',   label: t('shared.activity', 'Activity'),                              icon: 'activity' },
];

const SectionHeader = ({ icon, color = 'primary', title, subtitle }) => (
  <div className="d-flex align-items-center mb-3">
    <div className="info-icon me-3">
      <div
        className={`bg-${color} bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center`}
        style={{ width: '40px', height: '40px' }}
      >
        <FeatherIcon icon={icon} size={20} className={`text-${color}`} />
      </div>
    </div>
    <div>
      <h6 className="mb-1 fw-bold">{title}</h6>
      {subtitle && <p className="text-muted small mb-0">{subtitle}</p>}
    </div>
  </div>
);

const InfoItem = ({ icon, label, last, children }) => (
  <div className={`info-item ${last ? 'mb-0' : 'mb-3'}`}>
    <div className="d-flex align-items-center mb-1">
      <FeatherIcon icon={icon} size={14} className="text-muted me-2" />
      <span className="fw-semibold text-muted small">{label}</span>
    </div>
    {typeof children === 'string' || typeof children === 'number'
      ? <p className="mb-0 text-dark">{children || '—'}</p>
      : (children ?? <p className="mb-0 text-dark">—</p>)}
  </div>
);

const EmptyState = ({ icon, title, description }) => (
  <div className="text-center py-5">
    <div
      className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
      style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}
    >
      <FeatherIcon icon={icon} size={24} className="text-muted" />
    </div>
    <h6 className="text-muted">{title}</h6>
    {description && <p className="text-muted mb-0">{description}</p>}
  </div>
);

const renderActivityPayload = (entry, t) => {
  if (entry.kind === 'STATUS_CHANGE') {
    return (
      <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
        {entry.fromStatus && <MaterioStatusBadge status={entry.fromStatus} />}
        {entry.fromStatus && <FeatherIcon icon="arrow-right" size={14} className="text-muted" />}
        <MaterioStatusBadge status={entry.toStatus} />
        {entry.note && <small className="text-muted ms-2">{entry.note}</small>}
      </div>
    );
  }
  let payload = null;
  if (entry.activityData) {
    try {
      payload = typeof entry.activityData === 'string' ? JSON.parse(entry.activityData) : entry.activityData;
    } catch (e) { /* ignore */ }
  }
  if (payload?.type === 'KEYED_MESSAGE') {
    return <span>{t(payload.key, payload.key)}</span>;
  }
  if (payload?.type === 'FIELD_CHANGES') {
    return (
      <div>
        <strong>{t('activity.materio.field_changes', 'Fields updated')}:</strong>
        <ul className="mb-0 mt-1 ps-3">
          {(payload.changes || []).map((c, i) => (
            <li key={i}>
              <code>{c.field}</code>: <em>{String(c.from ?? '—')}</em> → <strong>{String(c.to ?? '—')}</strong>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return <span>{entry.activity || '—'}</span>;
};

const AscendingViewTabs = ({
  record, activity, t,
  activeTab, setActiveTab,
  canSelectStatus, onStatusSelect,
  onAssetClick,
}) => {
  if (!record) return null;
  const tabs = TAB_DEFS(t);
  const counts = {
    assets: (record.linkedAssets || []).length || (record.linkedAssetIds || []).length || 0,
    activity: (activity || []).length,
  };
  const yn = (b) => b === true ? t('shared.yes', 'Yes') : b === false ? t('shared.no', 'No') : '—';

  return (
    <>
      <Nav tabs className="nav-tabs-custom mb-0 px-4">
        {tabs.map((tab) => (
          <NavItem key={tab.key}>
            <NavLink
              className={activeTab === tab.key ? 'active' : ''}
              onClick={() => setActiveTab(tab.key)}
              style={{ cursor: 'pointer' }}
            >
              <FeatherIcon icon={tab.icon} size={16} className="me-2" />
              {tab.label}
              {counts[tab.key] > 0 && (
                <Badge color="primary" pill className="ms-2">{counts[tab.key]}</Badge>
              )}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      <TabContent activeTab={activeTab} className="h-100">

        {/* DETAILS */}
        <TabPane tabId="details" className="h-100">
          <div className="p-4">
            <Row className="g-4 mb-4">
              <Col md={6}>
                <motion.div variants={cardVariants} initial="hidden" animate="visible" className="h-100">
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="p-4">
                      <SectionHeader
                        icon="info"
                        color="primary"
                        title={t('shared.basic_information', 'Basic Information')}
                        subtitle={t('compliance.materiovigilance.section.alert_subtitle', 'Core alert details')}
                      />
                      <div className="info-grid">
                        <InfoItem icon="hash" label={t('compliance.materiovigilance.field.alert_number', 'Alert Number')}>
                          <p className="mb-0 text-dark fw-semibold">{record.alertNumber || '—'}</p>
                        </InfoItem>
                        <InfoItem icon="calendar" label={t('compliance.materiovigilance.ascending.field.incident_date', 'Date of Incident')}>
                          {record.incidentDate ? formatCreatedDate(record.incidentDate) : '—'}
                        </InfoItem>
                        <InfoItem icon="user" label={t('compliance.materiovigilance.ascending.field.issuer_user', 'Created By')} last>
                          {record.issuerUserName || '—'}
                        </InfoItem>
                      </div>
                    </CardBody>
                  </Card>
                </motion.div>
              </Col>

              <Col md={6}>
                <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }} className="h-100">
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="p-4">
                      <SectionHeader
                        icon="alert-triangle"
                        color="warning"
                        title={t('compliance.materiovigilance.section.incident', 'Incident')}
                        subtitle={t('compliance.materiovigilance.section.incident_subtitle', 'Patient and device impact')}
                      />
                      <div className="info-grid">
                        <InfoItem icon="repeat" label={t('compliance.materiovigilance.ascending.field.is_recurring', 'Recurring?')}>
                          <Badge color={record.isRecurring ? 'warning' : 'light'} className={record.isRecurring ? '' : 'text-dark border'} style={{ fontSize: 11, padding: '4px 8px' }}>
                            {yn(record.isRecurring)}
                          </Badge>
                        </InfoItem>
                        <InfoItem icon="users" label={t('compliance.materiovigilance.ascending.field.patient_count', 'Patients Affected')}>
                          {record.patientCount ?? '—'}
                        </InfoItem>
                        <InfoItem icon="activity" label={t('compliance.materiovigilance.ascending.field.has_clinical_implications', 'Clinical Implications?')}>
                          <Badge color={record.hasClinicalImplications ? 'danger' : 'light'} className={record.hasClinicalImplications ? '' : 'text-dark border'} style={{ fontSize: 11, padding: '4px 8px' }}>
                            {yn(record.hasClinicalImplications)}
                          </Badge>
                        </InfoItem>
                        <InfoItem icon="droplet" label={t('compliance.materiovigilance.ascending.field.is_biologically_contaminated', 'Biologically Contaminated?')}>
                          <Badge color={record.isBiologicallyContaminated ? 'danger' : 'light'} className={record.isBiologicallyContaminated ? '' : 'text-dark border'} style={{ fontSize: 11, padding: '4px 8px' }}>
                            {yn(record.isBiologicallyContaminated)}
                          </Badge>
                        </InfoItem>
                        <InfoItem icon="power" label={t('compliance.materiovigilance.ascending.field.still_in_use', 'Still in Use?')} last>
                          <Badge color={record.stillInUse ? 'success' : 'secondary'} style={{ fontSize: 11, padding: '4px 8px' }}>
                            {yn(record.stillInUse)}
                          </Badge>
                        </InfoItem>
                      </div>
                    </CardBody>
                  </Card>
                </motion.div>
              </Col>
            </Row>

            <Row className="g-4">
              <Col md={12}>
                <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-4">
                      <SectionHeader
                        icon="file-text"
                        color="secondary"
                        title={t('compliance.materiovigilance.ascending.field.description', 'Description')}
                        subtitle={t('compliance.materiovigilance.section.description_subtitle', 'Full alert description')}
                      />
                      <div className="bg-light rounded p-3">
                        <p className="mb-0 text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                          {record.description || t('shared.no_description_provided', 'No description provided')}
                        </p>
                      </div>
                    </CardBody>
                  </Card>
                </motion.div>
              </Col>
            </Row>
          </div>
        </TabPane>

        {/* MANAGEMENT */}
        <TabPane tabId="management" className="h-100">
          <div className="p-4">
            <motion.div variants={cardVariants} initial="hidden" animate="visible">
              <Card className="border-0 shadow-sm mb-4">
                <CardBody className="p-4">
                  <div className="d-flex align-items-center mb-4">
                    <div className="info-icon me-3">
                      <div
                        className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: '40px', height: '40px' }}
                      >
                        <FeatherIcon icon="settings" size={20} className="text-primary" />
                      </div>
                    </div>
                    <div>
                      <h5 className="mb-1 fw-bold">
                        {t('compliance.materiovigilance.section.status_management', 'Status Management')}
                      </h5>
                      <p className="text-muted small mb-0">
                        {t('compliance.materiovigilance.section.status_management_sub', 'Update alert status and workflow')}
                      </p>
                    </div>
                  </div>

                  <div className="status-buttons-grid">
                    <Row className="g-3">
                      {STATUS_OPTIONS(t).map((item) => {
                        const isActive = record.status === item.value;
                        const enabled = canSelectStatus(item.value);
                        return (
                          <Col md={6} lg={4} key={item.value}>
                            <motion.button
                              type="button"
                              whileHover={enabled && !isActive ? { scale: 1.02 } : undefined}
                              whileTap={enabled && !isActive ? { scale: 0.98 } : undefined}
                              disabled={!enabled || isActive}
                              className={`status-option-btn w-100 d-flex align-items-center p-3 rounded-3 border ${
                                isActive
                                  ? 'border-primary bg-primary text-white shadow-sm'
                                  : 'border-light bg-white text-dark hover-shadow'
                              }`}
                              style={{
                                borderWidth: '2px',
                                cursor: enabled && !isActive ? 'pointer' : 'not-allowed',
                                opacity: !enabled && !isActive ? 0.5 : 1,
                                transition: 'all 0.3s ease',
                              }}
                              onClick={() => onStatusSelect(item.value)}
                            >
                              <div
                                className={`status-icon me-3 rounded-circle d-flex align-items-center justify-content-center ${
                                  isActive ? 'bg-white bg-opacity-10' : 'bg-light'
                                }`}
                                style={{ width: '40px', height: '40px' }}
                              >
                                <FeatherIcon
                                  icon={item.icon}
                                  size={18}
                                  className={isActive ? 'text-white' : 'text-muted'}
                                />
                              </div>
                              <div className="text-start">
                                <div className="fw-semibold">{item.label}</div>
                                <small className={isActive ? 'text-white-50' : 'text-muted'}>
                                  {item.description}
                                </small>
                              </div>
                              {isActive && (
                                <div className="ms-auto">
                                  <FeatherIcon icon="check" size={18} className="text-white" />
                                </div>
                              )}
                            </motion.button>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                </CardBody>
              </Card>
            </motion.div>

            <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
              <Card className="border-0 shadow-sm">
                <CardBody className="p-4">
                  <SectionHeader
                    icon="users"
                    color="info"
                    title={t('compliance.materiovigilance.ascending.field.recipients', 'Recipients')}
                    subtitle={t('compliance.materiovigilance.section.recipients_subtitle', 'People notified about this alert')}
                  />
                  {(record.recipientUsers && record.recipientUsers.length) ? (
                    <div className="users-grid">
                      {record.recipientUsers.map((u, i) => (
                        <motion.div
                          key={u.id}
                          className="user-card bg-light rounded p-3 mb-3"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.05 }}
                        >
                          <div className="d-flex align-items-center">
                            <div className="me-3">
                              <div
                                className="bg-info bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: '48px', height: '48px' }}
                              >
                                <FeatherIcon icon="user" size={20} className="text-info" />
                              </div>
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-semibold">{u.name}</h6>
                              {u.role && (
                                <Badge color="light" className="text-muted">
                                  {String(u.role).replace('ROLE_', '').replace(/_/g, ' ')}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState
                      icon="user-x"
                      title={t('compliance.materiovigilance.ascending.field.recipients_empty', 'No recipients selected')}
                      description={t('compliance.materiovigilance.section.recipients_empty_desc', 'Add recipients while editing this alert.')}
                    />
                  )}
                </CardBody>
              </Card>
            </motion.div>
          </div>
        </TabPane>

        {/* ASSETS */}
        <TabPane tabId="assets" className="h-100">
          <div className="p-4">
            <Row>
              <Col>
                <motion.div variants={cardVariants} initial="hidden" animate="visible">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-4">
                      <SectionHeader
                        icon="package"
                        color="success"
                        title={t('compliance.materiovigilance.ascending.field.linked_assets', 'Assets')}
                        subtitle={t('compliance.materiovigilance.section.assets_subtitle', 'Equipment linked to this alert')}
                      />
                      {(record.linkedAssets && record.linkedAssets.length) ? (
                        <AssetListTable
                          data={record.linkedAssets}
                          excludeColumns={['location']}
                          onAssetClick={onAssetClick}
                          emptyMessage={t('compliance.materiovigilance.section.assets_empty', 'No Assets Linked')}
                          emptySubMessage={t('compliance.materiovigilance.section.assets_empty_desc', 'Equipment linked to this alert will appear here.')}
                        />
                      ) : (
                        <EmptyState
                          icon="package"
                          title={t('compliance.materiovigilance.section.assets_empty', 'No Assets Linked')}
                          description={t('compliance.materiovigilance.section.assets_empty_desc', 'Equipment linked to this alert will appear here.')}
                        />
                      )}
                    </CardBody>
                  </Card>
                </motion.div>
              </Col>
            </Row>
          </div>
        </TabPane>

        {/* ACTIVITY */}
        <TabPane tabId="activity" className="h-100">
          <div className="p-4">
            <Row>
              <Col>
                <motion.div variants={cardVariants} initial="hidden" animate="visible">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-4">
                      <SectionHeader
                        icon="activity"
                        color="primary"
                        title={t('shared.activity_timeline', 'Activity Timeline')}
                        subtitle={t('compliance.materiovigilance.section.activity_subtitle', 'Audit trail for this alert')}
                      />
                      <div className="activity-timeline" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {(activity && activity.length) ? (
                          <div className="timeline">
                            {activity.map((entry, i) => {
                              const time = entry.at
                                ? new Date(entry.at).toLocaleTimeString(navigator.language || 'en-IN', { hour: '2-digit', minute: '2-digit' })
                                : '';
                              return (
                                <motion.div
                                  key={`${entry.kind}-${entry.id}`}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: i * 0.05 }}
                                  className="timeline-item mb-4"
                                  style={{ position: 'relative', paddingLeft: '40px' }}
                                >
                                  <div
                                    className="timeline-dot bg-primary"
                                    style={{
                                      position: 'absolute',
                                      left: '0',
                                      top: '8px',
                                      width: '12px',
                                      height: '12px',
                                      borderRadius: '50%',
                                      border: '3px solid #fff',
                                      boxShadow: '0 0 0 2px #0d6efd',
                                    }}
                                  />
                                  {i < activity.length - 1 && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        left: '5px',
                                        top: '20px',
                                        width: '2px',
                                        height: 'calc(100% + 16px)',
                                        backgroundColor: '#e9ecef',
                                      }}
                                    />
                                  )}
                                  <div className="bg-light rounded p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <div className="d-flex align-items-center">
                                        <FeatherIcon icon="user" size={14} className="text-muted me-2" />
                                        <span className="fw-semibold text-dark">
                                          {entry.userName || t('shared.system', 'System')}
                                        </span>
                                      </div>
                                      <small className="text-muted">
                                        {entry.at && formatCreatedDate(entry.at)}
                                        {time && <span className="ms-1">{time}</span>}
                                      </small>
                                    </div>
                                    <div className="text-secondary small">
                                      {renderActivityPayload(entry, t)}
                                    </div>
                                  </div>
                                </motion.div>
                              );
                            })}
                          </div>
                        ) : (
                          <EmptyState
                            icon="message-square"
                            title={t('compliance.materiovigilance.section.activity_empty', 'No Activity Yet')}
                            description={t('compliance.materiovigilance.section.activity_empty_desc', 'Updates and changes will appear here over time.')}
                          />
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </motion.div>
              </Col>
            </Row>
          </div>
        </TabPane>
      </TabContent>
    </>
  );
};

export default MaterioAscendingModal;
